import React from 'react';
import { BufferMonthData } from './StrategiesUtils';
import { IMassUnitObject } from '../../../sp-types/massUnitType';

interface Props {
  bufferData: BufferMonthData[];
  matPrice: number;
  unit: IMassUnitObject;
}

export const BufferStock: React.FC<Props> = ({ bufferData, matPrice, unit }) => {
  // Get current month data (first item in the array)
  const currentMonthData = bufferData?.[0];
  if (!currentMonthData) {
    return (
      <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md items-center p-3">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  const storageValue = currentMonthData.storage * matPrice;
  const poValue = currentMonthData.po * matPrice;
  const bufferValue = currentMonthData.buffer * matPrice;
  const expectedDemandValue = currentMonthData.expectedDemand * matPrice;

  // Calculate the maximum value for scaling with a minimum scale
  const calculatedMax = Math.max(
    currentMonthData.storage + currentMonthData.po,
    currentMonthData.expectedDemand,
    currentMonthData.buffer,
  );

  // Use a reasonable maximum or add 20% padding for better visualization
  const maxValue = calculatedMax * 1.2;

  // Calculate bar widths as percentages
  const storageWidth = (currentMonthData.storage / maxValue) * 100;
  const poWidth = (currentMonthData.po / maxValue) * 100;
  const expectedDemandWidth = (currentMonthData.expectedDemand / maxValue) * 100;
  const bufferPosition = (currentMonthData.buffer / maxValue) * 100;

  return (
    <div className="min-w-[25rem] flex flex-col flex-1 bg-white shadow-md items-center p-3">
      <h3 className="w-full text-lg2 py-2">
        <p>
          Current buffer stock
          <i className="px-3 fas fa-info-circle" />
        </p>
      </h3>

      {/* Chart Area */}
      <div className="w-full flex flex-col gap-4 pb-3 mb-1 border-l border-b border-gray-300">
        {/* Current Supply Bar */}
        <div className="w-full">
          <div className="text-sm text-gray-600 mb-2">Current storage</div>
          <div className="relative h-8 rounded bg-gray-75">
            {/* Storage segment */}
            <div className="absolute h-full bg-blue-800" style={{ width: `${storageWidth}%` }} />
            {/* PO segment */}
            <div
              className="absolute h-full bg-blue-300 rounded-r"
              style={{
                width: `${poWidth}%`,
                left: `${storageWidth}%`,
              }}
            />
            {/* Buffer stock indicator line */}
            <div className="absolute h-full w-0.5 bg-red-500 z-10" style={{ left: `${bufferPosition}%` }} />
          </div>
        </div>

        {/* Expected Demand Bar */}
        <div className="w-full">
          <div className="text-sm text-gray-600 mb-2">Expected Demand (MRP + Buffer)</div>
          <div className="relative h-8 rounded bg-gray-75">
            {/* Expected demand outline */}
            <div
              className="absolute h-full border-y-2 border-r-2 border-dashed border-gray-400 rounded-r bg-gray-100"
              style={{ width: `${expectedDemandWidth}%` }}
            />
            {/* Buffer stock indicator line */}
            <div className="absolute h-full w-0.5 bg-red-500 z-10" style={{ left: `${bufferPosition}%` }} />
          </div>
        </div>
      </div>
      {/* X-axis */}
      <div className="w-full">
        <div className="flex justify-between text-xs text-gray-500">
          {Array.from({ length: 6 }, (_, i) => Math.round((maxValue / 5) * i)).map((value, idx) => (
            <span key={idx}>{value.toFixed(0)}</span>
          ))}
        </div>
        <div className="text-sm flex justify-center text-gray-600 mb-1">Metric tons</div>
      </div>

      {/* Legend */}
      <div className="w-full space-y-2 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-800 rounded"></div>
          <span className="text-sm">
            Storage:{' '}
            <strong>
              {currentMonthData.storage.toFixed(0)}
              {unit?.displayName || 'mt'} (${storageValue.toLocaleString()})
            </strong>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-300 rounded"></div>
          <span className="text-sm">
            PO&apos;s:{' '}
            <strong>
              {currentMonthData.po.toFixed(0)}
              {unit?.displayName || 'mt'} (${poValue.toLocaleString()})
            </strong>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-sm">
            Buffer stock:{' '}
            <strong>
              {currentMonthData.buffer.toFixed(0)}
              {unit?.displayName || 'mt'} (${bufferValue.toLocaleString()})
            </strong>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-dashed border-gray-400 rounded"></div>
          <span className="text-sm">
            Expected Demand (MRP + Buffer):{' '}
            <strong>
              {currentMonthData.expectedDemand.toFixed(0)}
              {unit?.displayName || 'mt'} (${expectedDemandValue.toLocaleString()})
            </strong>
          </span>
        </div>
      </div>
    </div>
  );
};
